import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { plainTextToLexicalSync } from '@plato/ui-legacy/lib/rich-text/plain-text-to-lexical';
import {
    Collapsible,
    CollapsibleHeader,
    CollapsibleHeading,
    CollapsiblePanel,
    CollapsibleTitle,
    CollapsibleTrigger,
} from '@plato/ui/containers/collapsible';
import { PlusIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmTaskInputDTO, CrmTaskIntent } from '../../../server/dto/crm-task-input-dto.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import type { TaskBulkFormValues } from '../../tasks/components/task-bulk-form-dialog.tsx';
import { TaskBulkFormDialog } from '../../tasks/components/task-bulk-form-dialog.tsx';
import { useAddStagedTasks, useStagedTasks } from '../log-interaction-tasks-provider.tsx';
import { LogInteractionTaskCard } from './log-interaction-task-card.tsx';

type TaskBulkDialogProps = {
    title: string;
    initialValues: Partial<CrmTaskInputDTO>;
    onSubmit: (tasks: CrmTaskInputDTO[]) => void;
};

const TaskBulkDialog = DialogStateManager.create<TaskBulkDialogProps>(
    ({ title, initialValues, onSubmit }: TaskBulkDialogProps) => {
        const { visible, remove } = useDialog();
        const { assignedToId, ...shared } = initialValues;

        const bulkInitialValues: Partial<TaskBulkFormValues> = assignedToId
            ? { ...shared, assignedToIds: [assignedToId] }
            : shared;

        return (
            <TaskBulkFormDialog
                open={visible}
                onClose={remove}
                onSubmit={({ assignedToIds, ...values }) => {
                    onSubmit(assignedToIds.map((id) => ({ ...values, assignedToId: id })));
                    remove();
                }}
                isSubmitting={false}
                title={title}
                initialValues={bulkInitialValues}
            />
        );
    },
);

type LogInteractionTaskListProps = {
    customerId: string;
    customerName?: string;
};

export function LogInteractionTaskList({ customerId, customerName }: LogInteractionTaskListProps): ReactNode {
    const { t } = useTranslation('notes');
    const items = useStagedTasks();
    const addTasks = useAddStagedTasks();

    const openCreateTask = (description: string, taskIntent: CrmTaskIntent): void => {
        const title = t('tasks__form_create_title', { ns: 'tasks' });
        const initialValues: Partial<CrmTaskInputDTO> = {
            description: description ? (plainTextToLexicalSync(description) ?? undefined) : undefined,
            taskIntent,
            links: [
                {
                    modelType: SearchEntity.Customer,
                    modelId: customerId,
                    title: customerName ?? null,
                },
            ],
        };

        void DialogStateManager.show(TaskBulkDialog, {
            title,
            initialValues,
            onSubmit: addTasks,
        });
    };

    return (
        <Collapsible>
            <CollapsibleHeader>
                <CollapsibleHeading>
                    <CollapsibleTitle>{t('notes__tasks_section_title')}</CollapsibleTitle>
                </CollapsibleHeading>
                <CollapsibleTrigger />
            </CollapsibleHeader>
            <CollapsiblePanel>
                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant="subtle"
                        rounded
                        leftIcon={PlusIcon}
                        className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                        onClick={() => {
                            openCreateTask(t('notes__tasks_quick_action_call'), 'CALL');
                        }}
                    >
                        {t('notes__tasks_quick_action_call')}
                    </Button>
                    <Button
                        type="button"
                        variant="subtle"
                        rounded
                        leftIcon={PlusIcon}
                        className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                        onClick={() => {
                            openCreateTask(t('notes__tasks_quick_action_clarify_price'), 'CLARIFY_PRICE');
                        }}
                    >
                        {t('notes__tasks_quick_action_clarify_price')}
                    </Button>
                    <Button
                        type="button"
                        variant="subtle"
                        rounded
                        leftIcon={PlusIcon}
                        className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                        onClick={() => {
                            openCreateTask('', 'GENERIC');
                        }}
                    >
                        {t('notes__tasks_quick_action_task')}
                    </Button>
                </div>
                <div className="flex flex-col gap-2 pt-2">
                    {items.map((item) => (
                        <LogInteractionTaskCard key={item.key} item={item} />
                    ))}
                </div>
            </CollapsiblePanel>
        </Collapsible>
    );
}
